import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Modal, ModalBody, ModalHeader } from "@/Components/Common/Tailwind";
import StepProgressBar from "@/Components/Common/StepProgressBar";

import _default from "flatpickr/dist/l10n/ko.js";
import "flatpickr/dist/flatpickr.css";
import Flatpickr from "react-flatpickr";
import { cn } from "@/Components/ui/utils";
import { isAndroid, isIOS } from "react-device-detect";
import { Oval } from "react-loader-spinner";
import { Clock, Trash2, CalendarDays, ChevronRight, Expand } from "lucide-react";

import { API } from "../../api";
import { useUserStore } from "../../store/store";
import { getAccessToken } from "../../api/auth";
import campIcon from "../../assets/images/camp_icon.png";
import { SITE_ID, SUBDOMAIN } from "../../assets/meta/meta";
import { getToday } from "../../common/utils/dateUtil";
import { numberWithCommas } from "../../common/utils/format_util";
import moment from "moment";
import {
  formatReservationDateLimit,
  getReservationDateLimit,
  isDateWithinReservationDateLimit,
} from "./reservationDateWindow.mjs";
import { parsePackageTagline } from "./packageTagline.mjs";
import { getPackageImageSrc } from "./packageImage.mjs";
import PackageDetailModal from "@/Components/modal/PackageDetailModal";
import { filterUsablePasses, sortByExpiryAsc } from "../UserPage/AmazingPass/passUsability.mjs";
import { seatLayoutService } from "../../api/services/seat-layout.service";
import SeatLayoutCanvas from "./SeatLayoutCanvas";
import SeatLayoutZoomViewer from "./SeatLayoutZoomViewer";
import {
  decorateFlatpickrDay,
  getDisplayHolidayForDate,
  getDisplayHolidayMonthParams,
  getDisplayHolidayRows,
  isSiteDisplayHolidayDate,
  normalizeDisplayHolidayResponse,
  SITE_DISPLAY_HOLIDAY_CLOSED_MESSAGE,
} from "../../common/siteDisplayHolidays.mjs";

const android_link = "https://play.google.com/store/apps/details?id=com.yargeu.AmazingCamp&hl=ko-KR";
const ios_link = "https://apps.apple.com/us/app/amzingticket/id6447417241";
const STEP_DATE = 1;
const STEP_PRODUCT = 2;
const STEP_CHECKOUT = 3;
const KOREAN_WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
const formatKoreanDateWithWeekday = (date) => {
  if (!date) return "-";
  const targetDate = moment(date);
  return `${targetDate.format("YYYY년 M월 D일")} (${KOREAN_WEEKDAYS[targetDate.day()]})`;
};

// 이번 달 휴무일 — 항상 펼침. 정기휴무/지점휴무로 묶어 표시 (공휴일은 캘린더에만 표시)
const HolidaySummary = ({ rows }) => {
  if (!rows || rows.length === 0) return null;

  const fmt = (d) => d.slice(5).replace("-", ".");
  const recurring = rows.filter((r) => r.recurring);
  const siteDates = rows.filter((r) => r.date && (r.source === "site" || r.source === "offdays"));

  const groups = [
    { label: "정기휴무", color: "#9ca3af", show: recurring.length > 0, text: recurring.map((r) => r.memo).join(", ") },
    { label: "임시휴무", color: "#e16036", show: siteDates.length > 0, text: siteDates.map((r) => fmt(r.date)).join(" · ") },
  ].filter((g) => g.show);

  if (groups.length === 0) return null;

  return (
    <div className="px-1 animate-list-item-in">
      <div className="flex flex-col gap-2.5 rounded-2xl border border-gray-100 bg-white px-3.5 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <p className="text-[13px] font-bold text-gray-700 font-[Gmarket-M]">이번 달 휴무일</p>
        {groups.map((g) => (
          <div key={g.label} className="flex items-start gap-2 text-[12px]">
            <span className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: g.color }} />
            <span className="w-[52px] shrink-0 pt-px font-bold text-gray-700">{g.label}</span>
            <span className="flex-1 leading-relaxed text-gray-700">{g.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Calendar = ({
  membership_product_id = "",
  package_api = "/package/items/",
  skip_to_order = false,
  use_amazingpass = false,
  // 상위 헤더의 뒤로가기(‹)가 스텝 인지형으로 동작하도록, 스텝 뒤로가기 핸들러를 등록한다.
  // 핸들러가 true를 반환하면 "스텝 뒤로가기로 처리됨", false면 상위가 페이지 이탈을 담당.
  registerBackHandler
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialDateParam = searchParams.get("date");
  // ★447(B): 스토어가 넘겨준 ?package=<기존 상품 id> — 그 날짜 상품 목록에 있으면 처음 한 번 미리 골라 둠(없으면 지금처럼)
  const initialPackageParam = Number(searchParams.get("package")) || null;
  const appliedPackageRef = React.useRef(false);

  const [selectedDate, setSelectedDate] = useState(initialDateParam || "");
  const [hasTouchedDate, setHasTouchedDate] = useState(Boolean(initialDateParam));
  const [currentStep, setCurrentStep] = useState(initialDateParam ? STEP_PRODUCT : STEP_DATE);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(true);
  const [toggleId, setToggleId] = useState(-1);
  const [selectedSpotId, setSelectedSpotId] = useState([]);
  const [selectedWaitingSpotName, setSelectedWaitingSpotName] = useState("");
  const [refundAgreed, setRefundAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [pageTransitionClass, setPageTransitionClass] = useState("");
  const [displayHolidayData, setDisplayHolidayData] = useState(null);

  const [waitingModalVisible, setWaitingModalVisible] = useState(false);
  const [user, siteDetail] = useUserStore((state) => [state.user, state.siteDetail]);

  const isMembershipReservation = Boolean(membership_product_id);
  const reservationDateLimit = React.useMemo(() => {
    if (use_amazingpass) {
      return null;
    }

    return getReservationDateLimit({
      availableDateCount: isMembershipReservation
        ? siteDetail?.reserv_membership_available_date_count
        : siteDetail?.reserv_available_date_count,
      baseDate: getToday(),
    });
  }, [isMembershipReservation, siteDetail, use_amazingpass]);
  const reservationDateWindowLabel = isMembershipReservation
    ? "멤버십 예약 가능 날짜"
    : "예약 가능한 날짜";
  const isDateWithinSiteReservationWindow = React.useCallback(
    (date) =>
      use_amazingpass || isDateWithinReservationDateLimit(date, reservationDateLimit),
    [reservationDateLimit, use_amazingpass],
  );
  const showReservationDateLimitWarning = React.useCallback(() => {
    if (!reservationDateLimit) {
      return;
    }

    toast.warning(
      `${reservationDateWindowLabel}는 ${formatReservationDateLimit(reservationDateLimit)}까지입니다.`,
    );
  }, [reservationDateLimit, reservationDateWindowLabel]);

  // 어메이징패스 예약 플로우: 보유한 미사용 패스 목록 / 상품 상세 모달 상태
  const [ownedPasses, setOwnedPasses] = useState([]);
  // 보유 패스 로딩 완료 여부. 어메이징패스 상품 목록은 "패스 사용 가능" 필터를 적용하므로,
  // 로드 전에는 목록을 확정하지 않는다(일반 예약은 패스와 무관해 항상 true).
  const [passesReady, setPassesReady] = useState(() => !use_amazingpass);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  // 어메이징패스: 펼친 상품의 발행 좌석 배치도(좌표형 좌석). 없으면 기본 그리드로 폴백.
  const [seatLayout, setSeatLayout] = useState(null);
  const [seatLayoutStatus, setSeatLayoutStatus] = useState("idle"); // idle | loading | ready | fallback | error
  const [seatLayoutPackageId, setSeatLayoutPackageId] = useState(null);
  const [seatMapModalOpen, setSeatMapModalOpen] = useState(false);

  const loadDisplayHolidays = React.useCallback(async (year, month) => {
    if (!SITE_ID) return;
    try {
      const response = await API.get(`/sites/display-holidays/?site_id=${SITE_ID}&year=${year}&month=${month}`);
      setDisplayHolidayData(normalizeDisplayHolidayResponse(response.data));
    } catch (error) {
      console.error(error);
      setDisplayHolidayData(normalizeDisplayHolidayResponse());
    }
  }, []);

  const handleCalendarVisibleMonth = React.useCallback((_selectedDates, _dateStr, instance) => {
    if (!instance) return;
    loadDisplayHolidays(instance.currentYear, instance.currentMonth + 1);
  }, [loadDisplayHolidays]);

  const handleDate = (date) => {
    let _momentData = moment(date);
    let parsedDate = _momentData.format("YYYY-MM-DD");
    if (_momentData.isBefore(moment().startOf("day"), "day")) {
      toast.error("오늘 이전 날짜는 선택할 수 없습니다.");
      return null;
    }
    if (!isDateWithinSiteReservationWindow(parsedDate)) {
      showReservationDateLimitWarning();
      return null;
    }
    setSelectedDate(parsedDate);
    return parsedDate;
  };

  const abortRef = React.useRef(null);
  const flatpickrRef = React.useRef(null);
  const displayHolidayDataRef = React.useRef(null);

  // 어메이징패스 예약 플로우: 보유한 미사용 패스를 전 페이지 수집한다.
  useEffect(() => {
    if (!use_amazingpass) return undefined;
    let cancelled = false;
    const loadOwnedPasses = async () => {
      try {
        const accessToken = getAccessToken();
        const collected = [];
        let page = 1;
        // 안전장치: 최대 50페이지
        for (let i = 0; i < 50; i++) {
          const response = await API.get(`/precoupon/user/?page=${page}&state=사용안함`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const results = response.data?.results || [];
          collected.push(...results);
          const next = response.data?.next;
          const match = next ? String(next).match(/page=(\d+)/) : null;
          if (!match) break;
          page = Number(match[1]);
        }
        if (!cancelled) setOwnedPasses(collected);
      } catch (error) {
        console.error("보유 패스 조회 실패", error);
        if (!cancelled) setOwnedPasses([]);
      } finally {
        if (!cancelled) setPassesReady(true);
      }
    };
    loadOwnedPasses();
    return () => {
      cancelled = true;
    };
  }, [use_amazingpass]);

  // 어메이징패스: 상품을 펼치면 발행된 좌석 배치도를 조회한다.
  // 있으면 좌표형 좌석(SeatLayoutCanvas), 404/좌석없음/오류면 기본 좌석 그리드로 폴백한다.
  useEffect(() => {
    if (!use_amazingpass || toggleId === -1 || !selectedDate) {
      setSeatLayout(null);
      setSeatLayoutStatus("idle");
      setSeatLayoutPackageId(null);
      return undefined;
    }
    const pkgId = toggleId;
    let cancelled = false;
    setSeatLayoutPackageId(pkgId);
    setSeatLayoutStatus("loading");
    setSeatLayout(null);
    seatLayoutService
      .getPublishedByPackage(pkgId, { date: selectedDate })
      .then((res) => {
        if (cancelled) return;
        const layout = res.data;
        if (layout && Array.isArray(layout.seats) && layout.seats.length > 0) {
          setSeatLayout(layout);
          setSeatLayoutStatus("ready");
        } else {
          setSeatLayout(null);
          setSeatLayoutStatus("fallback");
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setSeatLayout(null);
        setSeatLayoutStatus(error?.response?.status === 404 ? "fallback" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [use_amazingpass, toggleId, selectedDate]);

  const requestPackageProducts = async (e) => {
    try {
      const abortController = new AbortController();
      const signal = abortController.signal;

      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = abortController;

      let parsedDate = handleDate(e[0]);
      if (!parsedDate) {
        return;
      }
      if (isSiteDisplayHolidayDate(parsedDate, displayHolidayDataRef.current, "reservation")) {
        return false;
      }
      setLoading(true);
      const response = await API.get(`${package_api}?date=${parsedDate}&subdomain=${SUBDOMAIN}${membership_product_id ? `&membership_product_id=${membership_product_id}` : ""}`, {
        signal: signal,
      });

      const data = response.data;

      setPackages(data);
      const preselect =
        !appliedPackageRef.current && initialPackageParam && Array.isArray(data) && data.some((p) => p?.id === initialPackageParam)
          ? initialPackageParam
          : -1;
      if (preselect !== -1) appliedPackageRef.current = true;
      setToggleId(preselect);
      setSelectedSpotId([]);
      setLoading(false);
      return true;
    } catch (error) {
      if (error.name !== "CanceledError" && error.name !== "AbortError") {
        setLoading(false);
      }
      console.error(error);
      return false;
    }
  };

  const requestWaiting = () => {
    const accessToken = getAccessToken();

    if (!accessToken) {
      toast.warning("로그인이 필요한 서비스 입니다.");
      navigate(`/login`);
      return;
    }

    const url = `/package/waiting-reservation/`;

    API.post(
      url,
      {
        date: selectedDate,
        packageId: toggleId,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    )
      .then((response) => response.data)
      .then((data) => {
        if (data.result === "ok") toast.error("선택한 예약에 대한 알람설정이 완료 되었습니다.");
        else if (data.result === "already waiting") toast.error("이미 알람설정을 한 예약건 입니다.");
        else toast.error("알람설정을 실패하였습니다.");
      })
      .catch((error) => console.log(error));

    setSelectedWaitingSpotName("");
    setWaitingModalVisible(false);
  };

  const getDetailPage = () => {
    if (use_amazingpass) {
      const _package = packages.find((x) => x.id === toggleId);
      const passesForProduct = sortByExpiryAsc(
        filterUsablePasses(ownedPasses, { product: _package, selectDate: selectedDate })
      );
      // 결제 단계 검증: 선택 좌석 수가 사용 가능한 패스 갯수를 초과하면 진행 불가
      if (selectedSpotId.length > passesForProduct.length) {
        toast.error(
          `선택한 좌석 수(${selectedSpotId.length}개)가 사용 가능한 패스 갯수(${passesForProduct.length}개)를 초과합니다.`
        );
        return;
      }
      navigate(`/amazingpass/order`, {
        state: {
          product: _package,
          selectDate: selectedDate,
          startDate: selectedDate,
          endDate: selectedDate,
          spotId: selectedSpotId,
          usablePasses: passesForProduct,
        },
      });
      return;
    }

    if (skip_to_order) {
      const _package = packages.find((x) => x.id === toggleId);
      navigate(`/order`, {
        state: {
          type: 0, // "package"
          product: _package,
          selectDate: selectedDate,
          startDate: selectedDate,
          endDate: selectedDate,
          spotId: selectedSpotId,
          options: [],
          quantity: 1, // 나중에 티켓 수량 선택 시 이용할 필드
          membership_product_id: membership_product_id,
        },
      });
      return;
    }

    navigate(`/package/${toggleId}?date=${selectedDate}`, {
      state: {
        product: _package,
        selectedDate,
        selectedSpotId,
      },
    });
  };

  const isSpotSelected = (spot) => {
    return selectedSpotId.filter((ele) => ele.id === spot.id).length === 1;
  };

  // 좌석 선택 토글: 기본 그리드와 좌표형 배치도(SeatLayoutCanvas)가 공유한다.
  const toggleSpotSelection = (spot) => {
    if (isSpotSelected(spot)) {
      setSelectedSpotId(selectedSpotId.filter((ele) => ele.id !== spot.id));
    } else {
      setSelectedSpotId([...selectedSpotId, { id: spot.id, name: spot.name }]);
    }
  };

  const isWaitState = () => {
    let selectedPackage = packages.find((x) => x.id === toggleId);
    if (toggleId !== -1 && selectedSpotId.length === 0 && selectedPackage.count <= selectedPackage.reserved) return true;
    else return false;
  };

  const selectedPackage = packages.find((x) => x.id === toggleId) || null;
  // 어메이징패스는 지정석(각 좌석=고유 spot) 전제로만 예약이 가능하다. 자유석은 백엔드 use-coupon 이
  // 가상 spot 1개에 uid(spot_id+날짜) 유니크 제약 때문에 다건 예약을 받지 못하므로 목록에서 제외한다.
  // 주의: 목록 응답(/package/items)의 시리얼라이저(PackageWithReservationSerializer)에는 has_spots 가
  // 없다. 대신 각 좌석의 is_free(자유석=true, build_spot_booking_state_for_date) 로 판별한다.
  // has_spots 는 상세조회 응답에만 있으므로 fallback 으로만 함께 검사한다. (일반/멤버십 플로우는 그대로 노출)
  // 어메이징패스 모드에서는 자유석 제외에 더해 "내 패스로 예약 가능한(사용 가능 패스 ≥ 1) 상품"만 노출한다.
  // 패스 로딩(passesReady) 전에는 필터를 보류하고, 리스트는 로딩 표시로 빈 목록 깜빡임을 막는다.
  const visiblePackages = React.useMemo(() => {
    if (!use_amazingpass) return packages;
    const spotEligible = packages.filter(
      (p) => p.has_spots !== false && !(p.spots || []).some((s) => s?.is_free)
    );
    if (!passesReady) return spotEligible;
    return spotEligible.filter(
      (p) => filterUsablePasses(ownedPasses, { product: p, selectDate: selectedDate }).length > 0
    );
  }, [use_amazingpass, packages, passesReady, ownedPasses, selectedDate]);
  const requiresSpotSelection = Boolean((skip_to_order || use_amazingpass) && selectedPackage);
  // 어메이징패스: 선택 상품/날짜에 사용 가능한 패스를 만료 임박 순으로 정렬 → 최대 선택 좌석 수
  const usablePasses = React.useMemo(
    () =>
      use_amazingpass && selectedPackage
        ? sortByExpiryAsc(filterUsablePasses(ownedPasses, { product: selectedPackage, selectDate: selectedDate }))
        : [],
    [use_amazingpass, selectedPackage, ownedPasses, selectedDate]
  );
  const usablePassCount = usablePasses.length;
  const exceedsPassLimit = use_amazingpass && selectedSpotId.length > usablePassCount;
  const selectedSiteHoliday = selectedDate
    ? getDisplayHolidayForDate(selectedDate, displayHolidayData, "reservation")
    : null;
  const selectedDateIsSiteHoliday = selectedSiteHoliday?.source === "site";
  const canMoveToProduct = Boolean(
    selectedDate &&
      !selectedDateIsSiteHoliday &&
      isDateWithinSiteReservationWindow(selectedDate),
  );
  // 어메이징패스: 선택 좌석 수가 사용 가능한 패스 갯수를 초과하면 다음 단계로 진행 불가(버튼 비활성).
  // exceedsPassLimit 은 비-amazingpass 플로우에선 항상 false 이므로 일반 예약엔 영향 없음.
  const canMoveToCheckout = Boolean(selectedPackage && (!requiresSpotSelection || selectedSpotId.length > 0) && !exceedsPassLimit);
  const canSubmitPayment = Boolean(canMoveToCheckout && refundAgreed && privacyAgreed);
  const isProductListStep = currentStep === STEP_PRODUCT && !selectedPackage;
  const isProductDetailStep = currentStep === STEP_PRODUCT && Boolean(selectedPackage);
  const showInlineProductCheckout = Boolean(skip_to_order || use_amazingpass);
  const showStickyBottomBar = currentStep === STEP_DATE || currentStep === STEP_CHECKOUT || (currentStep === STEP_PRODUCT && showInlineProductCheckout);

  // 어메이징패스는 헥사곤 브랜드색(#f6475f), 일반 예약은 주황(#e16036)을 강조색으로 쓴다.
  // (공유 렌더 부분에서 아래 상수를 cn()으로 조합해 모드별 색을 분기)
  const accentText = use_amazingpass ? "text-[#f6475f]" : "text-[#e16036]";
  const accentBg = use_amazingpass ? "bg-[#f6475f]" : "bg-[#e16036]";
  const accentBgActive = use_amazingpass ? "active:bg-[#e02e48]" : "active:bg-[#c84a23]";
  const accentBorder = use_amazingpass ? "border-[#f6475f]" : "border-[#e16036]";
  const accentBorderHover = use_amazingpass ? "hover:border-[#f6475f]" : "hover:border-[#e16036]";
  const accentTextHover = use_amazingpass ? "hover:text-[#f6475f]" : "hover:text-[#e16036]";
  const accentGroupHover = use_amazingpass ? "group-hover:text-[#f6475f]" : "group-hover:text-[#e16036]";
  // flatpickr 날짜 캘린더의 선택/hover/today 강조색 (아래 <style> template literal에 주입)
  const calAccent = use_amazingpass ? "#f6475f" : "#e16036";
  // 어메이징패스는 상품 목록에 "패스 사용 가능" 필터를 적용하므로, 패스 로딩까지 끝나야 목록이 확정된다.
  const productListLoading = loading || (use_amazingpass && !passesReady);
  const progressPercent = currentStep === STEP_DATE
    ? (hasTouchedDate && selectedDate ? 25 : 0)
    : isProductListStep
      ? 25
      : isProductDetailStep
        ? 75
        : 100;

  const handleNext = async () => {
    if (currentStep === STEP_DATE && canMoveToProduct) {
      const loaded = await requestPackageProducts([selectedDate]);
      if (!loaded) return;
      setCalendarOpen(false);
      setPageTransitionClass("animate-step-in-right");
      setCurrentStep(STEP_PRODUCT);
      return;
    }
    if (currentStep === STEP_PRODUCT && canMoveToCheckout) {
      setPageTransitionClass("animate-step-in-right");
      setCurrentStep(STEP_CHECKOUT);
    }
  };

  const handleBack = () => {
    if (currentStep === STEP_PRODUCT) {
      setPageTransitionClass("animate-step-in-left");
      setCurrentStep(STEP_DATE);
      return;
    }
    if (currentStep === STEP_CHECKOUT) {
      setPageTransitionClass("animate-step-in-left");
      setCurrentStep(STEP_PRODUCT);
      return;
    }
  };

  // 상위 헤더의 뒤로가기(‹)를 스텝 인지형으로 통합.
  // 진행 중인 스텝이 남아있으면 한 단계 뒤로가기로 소비(true), 첫 단계면 상위에 이탈을 위임(false).
  useEffect(() => {
    if (!registerBackHandler) return undefined;
    registerBackHandler(() => {
      if (currentStep > STEP_DATE) {
        handleBack();
        return true;
      }
      return false;
    });
    return () => registerBackHandler(null);
  }, [registerBackHandler, currentStep]);

  useEffect(() => {
    let isActive = true;
    const dateParam = searchParams.get("date");
    const initialDate = dateParam || getToday();
    const parsedDate = handleDate(initialDate);
    if (dateParam) {
      if (!parsedDate) {
        setSelectedDate("");
        setHasTouchedDate(false);
        setCalendarOpen(true);
        setCurrentStep(STEP_DATE);
        setToggleId(-1);
        setSelectedSpotId([]);
        return () => {
          isActive = false;
        };
      }
      setHasTouchedDate(true);
      requestPackageProducts([parsedDate]).then((loaded) => {
        if (!isActive || !loaded) {
          if (isActive) {
            setCalendarOpen(true);
            setCurrentStep(STEP_DATE);
          }
          return;
        }
        setCalendarOpen(false);
        setCurrentStep(STEP_PRODUCT);
      });
    } else {
      setHasTouchedDate(false);
      setCurrentStep(STEP_DATE);
      setCalendarOpen(true);
      setToggleId(-1);
      setSelectedSpotId([]);
      setRefundAgreed(false);
      setPrivacyAgreed(false);
    }

    return () => {
      isActive = false;
    };
  }, [searchParams]);

  useEffect(() => {
    const { year, month } = getDisplayHolidayMonthParams(initialDateParam || getToday());
    loadDisplayHolidays(year, month);
  }, [initialDateParam, loadDisplayHolidays]);

  useEffect(() => {
    displayHolidayDataRef.current = displayHolidayData;
    flatpickrRef.current?.flatpickr?.redraw?.();
  }, [displayHolidayData]);

  // 공휴일은 정상 영업일이라 별도 표시하지 않음 → 컴포넌트 rows에서도 제외해 정기/임시휴무가 잘리지 않도록 함
  const displayHolidayRows = getDisplayHolidayRows(displayHolidayData, "reservation", {
    includePublicHolidays: false,
  }).slice(0, 8);
  const calendarOptions = React.useMemo(() => ({
    locale: _default.ko,
    inline: true,
    mode: "single",
    dateFormat: "Y-m-d",
    minDate: "today",
    maxDate: reservationDateLimit || undefined,
    defaultDate: getToday(),
    disableMobile: true,
    disable: [
      (date) => isSiteDisplayHolidayDate(date, displayHolidayDataRef.current, "reservation"),
    ],
    onReady: handleCalendarVisibleMonth,
    onMonthChange: handleCalendarVisibleMonth,
    onYearChange: handleCalendarVisibleMonth,
    onDayCreate: (_dObj, _dateStr, _fp, dayElem) => {
      decorateFlatpickrDay(dayElem, dayElem.dateObj, displayHolidayDataRef.current, "reservation");
    },
  }), [handleCalendarVisibleMonth, reservationDateLimit]);

  return (
    <div className="px-6 py-4 bg-gray-100 min-h-screen antialiased [-webkit-font-smoothing:antialiased] [font-variant-numeric:tabular-nums]">
      <style>
        {`
        .animate-step-in-right {
          animation: stepSlideInRight 220ms ease-out;
        }
        .animate-step-in-left {
          animation: stepSlideInLeft 220ms ease-out;
        }
        @keyframes stepSlideInRight {
          from { opacity: 0.6; transform: translateX(18px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes stepSlideInLeft {
          from { opacity: 0.6; transform: translateX(-18px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-list-item-in {
          animation: listItemIn 320ms cubic-bezier(0.2, 0, 0, 1) both;
          will-change: transform, opacity;
        }
        @keyframes listItemIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .flatpickr-calendar {
          box-shadow: none !important;
          border: none !important;
          width: 100% !important;
          background: #fff !important;
        }
        .flatpickr-months {
          background: #fff !important;
        }
        .flatpickr-months .flatpickr-month {
          background: #fff !important;
          height: 58px;
          color: #000 !important;
        }
        .flatpickr-innerContainer {
            width: 100% !important;
            border-bottom: none !important;
            border-image: none !important;
            background: unset !important;
        }
        .flatpickr-rContainer {
            width: 100% !important;
        }
        .flatpickr-days {
          width: 100% !important;
          border: none !important;
          display: block !important;
          overflow: visible !important;
        }
        .dayContainer {
          width: 100% !important;
          min-width: 100% !important;
          max-width: 100% !important;
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          justify-items: center !important;
          border: none !important;
          border-style: none !important;
          border-image: none !important;
        }
        .dayContainer .flatpickr-day {
          flex: none !important;
          width: min(46px, 100%) !important;
          min-width: 0 !important;
          max-width: 46px !important;
          min-height: 0 !important;
          height: auto !important;
          aspect-ratio: 1 / 1 !important;
          box-sizing: border-box !important;
        }
        .flatpickr-day {
          border-radius: 50% !important;
          margin: 0 auto !important;
          position: relative !important;
          height: auto !important;
          width: min(46px, 100%) !important;
          min-height: 0 !important;
          min-width: 0 !important;
          max-width: 46px !important;
          aspect-ratio: 1 / 1 !important;
          line-height: normal !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .flatpickr-day.selected, .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.selected.inRange, .flatpickr-day.startRange.inRange, .flatpickr-day.endRange.inRange {
          background: ${calAccent} !important;
          border-color: ${calAccent} !important;
          color: #fff !important;
        }
        /* focus만으로는 채우지 않음 (모바일 탭 후 포커스 잔상이 선택처럼 보이는 문제 방지) */
        .flatpickr-day:focus:not(.selected) {
          background: transparent !important;
          border-color: transparent !important;
          color: inherit !important;
        }
        /* hover는 실제 마우스가 있는 기기에서만 (모바일 sticky hover로 인한 잔상 방지) */
        @media (hover: hover) {
          .flatpickr-day:hover {
            background: ${calAccent} !important;
            border-color: ${calAccent} !important;
            color: #fff !important;
          }
        }
        .flatpickr-day.today:not(.selected) {
          background: transparent !important;
          border-color: ${calAccent} !important;
          color: ${calAccent} !important;
        }
        @media (hover: hover) {
          .flatpickr-day.today:not(.selected):hover {
            background: transparent !important;
            border-color: ${calAccent} !important;
            color: ${calAccent} !important;
          }
        }
        .flatpickr-day.flatpickr-disabled,
        .flatpickr-day.flatpickr-disabled:hover,
        .flatpickr-day.flatpickr-disabled:focus {
          color: #d1d5db !important;
          background: transparent !important;
          border-color: transparent !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
        }
        /* 휴무/공휴일 표시: 텍스트 배지 대신 숫자 아래 작은 점(dot)으로 시각 노이즈 최소화 */
        .flatpickr-day.site-display-holiday-day::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: 6px;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 999px;
          background: #cbd5e1;
          pointer-events: none;
        }
        .flatpickr-day.site-display-holiday-recurring::after {
          background: #cbd5e1;
        }
        .flatpickr-day.site-display-holiday-site::after {
          background: #e16036;
        }
        .flatpickr-day.selected.site-display-holiday-day::after,
        .flatpickr-day.site-display-holiday-day:hover::after,
        .flatpickr-day.site-display-holiday-day:focus::after {
          background: rgba(255,255,255,0.95);
        }
        .flatpickr-current-month {
            left: 44px !important;
            right: 44px !important;
            width: auto !important;
            min-width: 0 !important;
            height: 58px !important;
            padding-top: 12px;
            font-size: 150%;
            font-weight: 600;
            font-family: "Gmarket-M" !important;
            color: #000 !important;
            background: transparent !important;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            gap: 4px;
            box-sizing: border-box !important;
            overflow: visible !important;
        }
        .flatpickr-current-month .flatpickr-monthDropdown-months {
            order: 2;
        }
        .flatpickr-current-month .numInputWrapper {
            order: 1;
            position: relative;
            display: inline-flex;
            align-items: center;
            flex: 0 0 auto;
            width: auto !important;
            overflow: visible !important;
        }
        .flatpickr-current-month .numInputWrapper::after {
            content: "년";
            font-family: "Gmarket-M" !important;
            font-weight: 700;
            margin-left: 2px;
        }
        .flatpickr-current-month .flatpickr-monthDropdown-months,
        .flatpickr-current-month input.cur-year {
            font-family: "Gmarket-M" !important;
            font-weight: 700 !important;
            pointer-events: none !important;
        }
        .flatpickr-current-month .numInputWrapper:hover,
        .flatpickr-current-month .numInputWrapper {
            pointer-events: none !important;
            background: transparent !important;
        }
        .flatpickr-current-month .numInputWrapper span.arrowUp,
        .flatpickr-current-month .numInputWrapper span.arrowDown {
            display: none !important;
        }
        .flatpickr-current-month .flatpickr-monthDropdown-months {
            color: #000 !important;
            background: transparent !important;
            border: none;
            -webkit-appearance: none;
            appearance: none;
            width: auto !important;
            min-width: 0 !important;
            flex: 0 0 auto;
        }
        .flatpickr-current-month input.cur-year {
            color: #000 !important;
            background: transparent !important;
            width: 4.4ch !important;
            min-width: 4.4ch !important;
            padding: 0 !important;
            text-align: right !important;
            box-sizing: content-box !important;
        }
        .flatpickr-next-month, .flatpickr-prev-month {
            padding-top: 10px;
            fill: #000 !important;
            color: #000 !important;
        }
        .flatpickr-next-month svg, .flatpickr-prev-month svg {
            fill: #000 !important;
        }
        .flatpickr-weekday {
            font-weight: 400;
            color: #888;
            font-size: 14px;
        }
        .flatpickr-weekdays {
            background: #fff !important;
        }
        span.flatpickr-weekday {
            background: #fff !important;
        }
        `}
      </style>


      {/* Header Section — 어메이징패스에서는 진행바 헤더 대신 상위 페이지(index.jsx)의 "패스로 예약" 고정 헤더(h-16)를 쓰므로 그 높이만큼만 자리 보정 */}
      {use_amazingpass ? (
        <div className="h-16" />
      ) : (
        <>
          <div className="fixed top-0 left-1/2 z-50 w-full max-w-[640px] -translate-x-1/2 border-b border-gray-200/70 bg-white/85 px-4 pb-3 pt-4 backdrop-blur-md">
            <h1 className="text-lg font-bold leading-tight text-gray-900 font-[Gmarket-B]">예약</h1>
            <StepProgressBar progressPercent={progressPercent} className="mt-3" />
          </div>

          <div className="h-[106px]" />
        </>
      )}

      <div
        className={cn("flex flex-col gap-6 justify-center pb-[320px]", pageTransitionClass)}
        onAnimationEnd={() => setPageTransitionClass("")}
      >
        {/* Calendar Card */}
        {currentStep === STEP_DATE && (
        <div className="flex flex-col gap-4">
        <HolidaySummary rows={displayHolidayRows} />
        <div className="bg-white rounded-3xl p-3 shadow-[0_2px_20px_rgba(0,0,0,0.04)] border border-gray-100/80 animate-list-item-in" style={{ animationDelay: "70ms" }}>
          {calendarOpen ? (
            <div className="mt-1">
              <Flatpickr
                ref={flatpickrRef}
                onChange={(e) => {
                  setHasTouchedDate(true);
                  handleDate(e[0]);
                }}
                value={selectedDate || getToday()}
                className="form-control hidden"
                style={{ display: "none" }}
                options={calendarOptions}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setCalendarOpen(true)}
              className="w-full flex flex-col gap-2 text-center cursor-pointer rounded-2xl py-2 transition-[scale,background-color] duration-200 active:scale-[0.96] active:bg-gray-50"
            >
              <p className="text-xs text-gray-700 mb-1 font-[Gmarket-M] flex items-center justify-center gap-1">
                <CalendarDays size={14} className="text-gray-700" />
                예약일 선택
              </p>
              <p className="text-lg font-bold font-[Gmarket-B] text-gray-900">
                {formatKoreanDateWithWeekday(selectedDate)}
              </p>
              <span className={cn("text-xs mt-1", accentText)}>날짜 다시 선택</span>
            </button>
          )}
        </div>
        {reservationDateLimit && (
          <p className="flex items-center gap-2 px-1 text-xs font-medium text-gray-600" aria-live="polite">
            <CalendarDays className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <span>
              {reservationDateWindowLabel}: 오늘 ~{" "}
              <strong className="font-semibold text-gray-900">
                {formatReservationDateLimit(reservationDateLimit)}
              </strong>
            </span>
          </p>
        )}
        {selectedDateIsSiteHoliday && (
          <div className={cn("flex items-start gap-2 rounded-2xl px-4 py-3 text-sm font-semibold", use_amazingpass ? "border border-[#f6475f]/20 bg-[#f6475f]/10 text-[#9f1239]" : "border border-[#f4c8cf] bg-[#fff7f3] text-[#9a3412]")}>
            <CalendarDays size={16} className="mt-0.5 shrink-0" />
            <span>{SITE_DISPLAY_HOLIDAY_CLOSED_MESSAGE}</span>
          </div>
        )}
        </div>
        )}

        {/* Available Products Section */}
        {(currentStep === STEP_PRODUCT || currentStep === STEP_CHECKOUT) && (
        <div className="mb-2">
          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
            <div className="flex items-center justify-between px-4 py-4">
              <div className="flex min-w-0 items-center gap-2.5 pr-3 text-gray-900">
                <CalendarDays size={16} className="shrink-0" />
                <p className="flex min-w-0 flex-wrap items-center gap-1.5 font-semibold font-[Gmarket-B] leading-tight">
                  <span>{selectedDate ? moment(selectedDate).format("YYYY년 M월 D일") : "-"}</span>
                  {selectedDate && (
                    <span className="text-sm font-[Gmarket-M] text-gray-900">
                      ({KOREAN_WEEKDAYS[moment(selectedDate).day()]})
                    </span>
                  )}
                </p>
              </div>
              <button
                type="button"
                className={cn("shrink-0 inline-flex min-h-10 items-center gap-1 rounded-lg border border-gray-400 bg-white px-3.5 py-2 text-xs font-[Gmarket-M] text-gray-600 transition-[scale,color,border-color] duration-200 active:scale-[0.96] focus:outline-none focus-visible:ring-2", accentBorderHover, accentTextHover, use_amazingpass ? "focus-visible:border-[#f6475f] focus-visible:ring-[#f6475f]/30" : "focus-visible:border-[#e16036] focus-visible:ring-[#f4c8cf]")}
                onClick={() => {
                  setPageTransitionClass("animate-step-in-left");
                  setCurrentStep(STEP_DATE);
                  setCalendarOpen(true);
                }}
              >
                변경
              </button>
            </div>
            <div className="border-t border-gray-100 px-4 py-5">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-gray-900" />
                <h3 className="text-sm font-[Gmarket-B]">
                  {productListLoading ? (
                    <span className="text-gray-500">예약 가능한 상품을 확인하고 있어요.</span>
                  ) : (
                    <>
                      <span className="text-base font-semibold text-gray-900">{visiblePackages.length}</span>건의 예약가능한 상품들이 있어요.
                    </>
                  )}
                </h3>
              </div>
            </div>

          <ul className="bg-white" role="list">
            {productListLoading ? (
              <li className="flex justify-center py-12 list-none">
                <Oval visible={true} height="40" width="40" color="#000" secondaryColor="#eee" ariaLabel="oval-loading" />
              </li>
            ) : visiblePackages.length !== 0 ? (
              visiblePackages.map((item, index) => {
                const isOpen = item.id === toggleId;
                const remainingCount = Math.max((item.count || 0) - (item.reserved || 0), 0);
                const isSoldOut = remainingCount <= 0 || (item.price === 0 && !item.allow_free_booking);
                const isLowStock = remainingCount > 0 && remainingCount <= 3;
                const hasMembershipPrice = user && user.membership !== "" && item.membership_price && item.membership_price != item.price;
                const thumbnailSrc = getPackageImageSrc(item.images?.[0]);
                const taglineBadges = parsePackageTagline(item.tagline);

                return (
                  <li
                    key={item.id}
                    id={`amazingpass-item-${item.id}`}
                    className={cn(
                      "list-none overflow-hidden scroll-mt-20 transition-[background-color,box-shadow] duration-500 animate-list-item-in",
                      isOpen ? "bg-[#fff8f5]" : "bg-white",
                      "border-b border-gray-200 last:border-b-0"
                    )}
                    style={{ animationDelay: `${Math.min(index * 55, 330)}ms` }}
                  >
                    <button
                      type="button"
                      className="group w-full px-4 py-5 text-left cursor-pointer transition-colors duration-150 active:bg-gray-50"
                      onClick={() => {
                        if (!(skip_to_order || use_amazingpass)) {
                          navigate(`/package/${item.id}?date=${selectedDate}`, {
                            state: {
                              product: item,
                              selectedDate,
                            },
                            preventScrollReset: true,
                          });
                          return;
                        }
                        if (use_amazingpass) {
                          // 상품 클릭 시 상세 정보를 모달로 표시 (확정하면 좌석 선택으로 전환)
                          setDetailProduct(item);
                          setDetailModalOpen(true);
                          return;
                        }
                        setToggleId(item.id === toggleId ? -1 : item.id);
                        setSelectedSpotId([]);
                      }}
                    >
                      <div className="flex flex-row items-center gap-3">
                        <div className="relative shrink-0 w-[88px] aspect-square rounded-2xl overflow-hidden bg-gray-100 ring-1 ring-inset ring-black/10">
                          {thumbnailSrc ? (
                            <img
                              src={thumbnailSrc}
                              className={cn("w-full h-full object-cover", isSoldOut ? "opacity-60" : "")}
                              alt={item.name}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <img src={campIcon} className="w-10 h-10 object-contain opacity-50" alt="default" />
                            </div>
                          )}
                          {isSoldOut ? (
                            <div className="absolute inset-0 bg-black/35" />
                          ) : null}
                        </div>
                        <div className="flex-1 min-w-0 px-1 justify-center flex flex-col gap-1 pt-1">
                          <h4 className="text-[15px] font-bold leading-tight text-gray-900 font-[Gmarket-B] line-clamp-2 text-pretty break-keep">{item.name}</h4>

                          <div className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs font-semibold">
                            {isSoldOut ? (
                              <span className="inline-flex items-center rounded-full bg-gray-300 px-2.5 py-1 text-[11px] font-semibold text-gray-900 ring-1 ring-gray-200">예약마감</span>
                            ) : (
                              <>
                                <span className={cn("whitespace-nowrap", isLowStock ? "text-[#e16036]" : "text-[#15803d]")}>
                                  잔여 {remainingCount}석
                                </span>
                                <span className="text-gray-300">/</span>
                                <span className="whitespace-nowrap text-gray-700">총 {item.count || 0}석</span>
                              </>
                            )}
                          </div>
                          {/* 어메이징패스는 패스 1:1 교환이라 가격을 노출하지 않는다. */}
                          {!use_amazingpass && (
                            <div className="flex flex-wrap items-baseline justify-start gap-x-2 gap-y-1 pt-0.5 text-left">
                              {isSoldOut ? (
                                null
                              ) : hasMembershipPrice ? (
                                <>
                                  <span className="text-[11px] text-gray-400 line-through">{numberWithCommas(item.price)}원</span>
                                  <span className="text-base font-extrabold text-[#22c55e] font-[Gmarket-B]">{numberWithCommas(item.membership_price)}원</span>
                                </>
                              ) : (
                                <span className="text-base font-bold font-[Gmarket-B] text-gray-900">{numberWithCommas(item.price)}원</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div
                          className={cn(
                            "mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-[color,transform] duration-200 group-active:translate-x-0.5",
                           isSoldOut ? "text-gray-300" : cn("text-gray-700", accentGroupHover)
                          )}
                        >
                          <ChevronRight size={18} />
                        </div>
                      </div>

                      {taglineBadges.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                          {taglineBadges.map((tagline, index) => (
                            <span
                              key={`${item.id}-tagline-${index}-${tagline}`}
                              className={cn("inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-[11px] font-semibold leading-4 break-keep", use_amazingpass ? "border border-[#f6475f]/20 bg-[#f6475f]/10 text-[#9f1239]" : "border border-[#f4c8cf] bg-[#fff7f3] text-[#9a3412]")}
                            >
                              {tagline}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </button>

                    {/* Spots Section */}
                    {isOpen && (
                      <div className="border-t border-gray-100 bg-white p-4 pt-0">
                        <div className="flex items-center justify-between gap-2 py-3">
                          <span className="flex items-center gap-2 text-[15px] font-bold text-gray-900 font-[Gmarket-M]">
                            <span className={cn("h-3.5 w-1 rounded-full", accentBg)} />
                            {requiresSpotSelection ? "좌석 선택" : "좌석/옵션 선택"}
                          </span>
                          {use_amazingpass && seatLayoutPackageId === item.id && seatLayoutStatus === "ready" && seatLayout ? (
                            <button
                              type="button"
                              className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[#f6475f]/10 px-3 py-1.5 text-xs font-bold text-[#f6475f] transition-[scale,background-color] duration-150 hover:bg-[#f6475f]/15 active:scale-[0.96]"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSeatMapModalOpen(true);
                              }}
                            >
                              <Expand size={13} strokeWidth={2.4} />
                              크게 보기
                            </button>
                          ) : null}
                        </div>
                        {use_amazingpass && seatLayoutPackageId === item.id && seatLayoutStatus === "loading" ? (
                          <div className="flex items-center justify-center py-10">
                            <Oval visible height="32" width="32" color="#f6475f" secondaryColor="#f4d5c9" ariaLabel="seat-layout-loading" />
                          </div>
                        ) : use_amazingpass && seatLayoutPackageId === item.id && seatLayoutStatus === "ready" && seatLayout ? (
                          <div className="max-h-[420px] overflow-auto">
                            <SeatLayoutCanvas
                              layout={seatLayout}
                              selectedSeatIds={selectedSpotId.map((s) => s.id)}
                              onSelectSeat={(seat) => toggleSpotSelection({ id: seat.spot_id, name: seat.label || seat.spot_name })}
                              autoFit
                            />
                          </div>
                        ) : (
                          <div className="grid grid-cols-4 gap-2">
                            {item.spots.map((spot) => {
                              const selected = isSpotSelected(spot);
                              const disabled = !spot["is_able"] || (item.price === 0 && !item.allow_free_booking);
                              return (
                                <button
                                  key={spot.id}
                                  className={cn(
                                    "relative py-3 rounded-xl text-sm font-bold border-2 transition-[scale,background-color,border-color,box-shadow] duration-200",
                                    selected && cn(accentBg, accentBorder, "text-white shadow-md scale-[1.02]"),
                                    !selected && !disabled && cn("bg-gray-50 text-gray-900 border-gray-300 hover:bg-white active:scale-[0.96]", accentBorderHover),
                                    disabled && "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (disabled) return;
                                    toggleSpotSelection({ id: spot.id, name: spot.name });
                                  }}
                                  disabled={disabled}
                                >
                                  {!disabled ? spot.name : "X"}
                                  {selected && (
                                    <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-[#f6475f] rounded-full"></div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {use_amazingpass && (
                          <p className="mt-3 px-1 text-[11px] leading-relaxed text-gray-700">
                            만료기한이 임박한 패스권부터 자동으로 사용됩니다.
                          </p>
                        )}
                        {!requiresSpotSelection && (
                          <p className="mt-3 text-xs text-gray-500">옵션 선택은 다음 상세 단계에서 진행됩니다.</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="flex list-none flex-col items-center justify-center gap-3 bg-white py-16">
                <img src={campIcon} className="w-12 h-12 object-contain opacity-40" alt="empty" />
                {use_amazingpass ? (
                  <div className="flex flex-col items-center gap-1 px-6 text-center">
                    <p className="text-sm font-bold text-gray-700 font-[Gmarket-M]">이 날짜에 패스로 예약할 수 있는 상품이 없어요</p>
                    <p className="text-xs leading-relaxed text-gray-700 text-pretty">보유하신 패스의 사용 가능 상품·기간을 확인하거나 다른 날짜를 선택해 주세요.</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 font-[Gmarket-M]">이 날짜에 예약 가능한 상품이 없습니다</p>
                )}
              </li>
            )}
          </ul>
          </div>
        </div>
        )}

        {currentStep === STEP_CHECKOUT && selectedPackage && (
          <div className="bg-white rounded-3xl p-5 border border-gray-100 shadow-[0_2px_20px_rgba(0,0,0,0.04)]">
            <h4 className="text-lg font-[Gmarket-B] mb-4">결제 확인</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">예약일</span><span>{formatKoreanDateWithWeekday(selectedDate)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">상품</span><span>{selectedPackage.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">수량</span><span>{requiresSpotSelection ? selectedSpotId.length : 1}개</span></div>
              {requiresSpotSelection && (
                <div className="flex justify-between"><span className="text-gray-500">자리</span><span className="text-right">{selectedSpotId.map((s) => s.name).join(", ") || "-"}</span></div>
              )}
              <div className="flex justify-between font-[Gmarket-B] text-base pt-2 border-t border-gray-100">
                <span>총 결제금액</span>
                <span className="text-[#e16036]">{numberWithCommas((selectedPackage.price || 0) * (requiresSpotSelection ? selectedSpotId.length : 1))}원</span>
              </div>
            </div>
            <div className="mt-5 p-3 rounded-xl bg-orange-50 text-xs text-gray-700">
              환불 규정: 사용 전 취소 시 환불 가능하며, 사용 후 환불은 제한될 수 있습니다.
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-[#e16036]" checked={refundAgreed} onChange={(e) => setRefundAgreed(e.target.checked)} />
              환불 규정에 동의합니다. (필수)
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-[#e16036]" checked={privacyAgreed} onChange={(e) => setPrivacyAgreed(e.target.checked)} />
              개인정보 및 결제 진행에 동의합니다. (필수)
            </label>
          </div>
        )}

      </div>

      {/* Sticky Bottom Bar */}
      {showStickyBottomBar && (
        <div
          className="fixed left-1/2 -translate-x-1/2 w-full max-w-[640px] z-[100] px-4 pb-3 pt-2 bg-gradient-to-t from-gray-100 via-gray-100/95 to-transparent"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 110px)" }}
        >
          <div
            className={cn(
              "flex flex-col",
              currentStep === STEP_DATE
                ? "gap-3"
                : "gap-4 bg-white/90 backdrop-blur-md rounded-3xl border border-gray-100/70 shadow-[0_-4px_24px_rgba(0,0,0,0.04),0_-18px_60px_-12px_rgba(0,0,0,0.10)] p-4"
            )}
          >
            {currentStep !== STEP_DATE && selectedPackage && (
              <div className="flex items-center justify-between px-2">
                <div className="flex flex-col min-w-0 pr-3">
                  <span className="text-base font-bold text-gray-900 truncate font-[Gmarket-B]">{selectedPackage?.name || "-"}</span>
                </div>
                <div className="text-right shrink-0">
                  {/* 어메이징패스는 패스 1:1 교환이라 결제금액 대신 '선택 좌석 / 보유 패스' 비교를 보여준다.
                      좌석 선택 모달(선택 N / 사용가능 패스 M)과 동일한 위계로 통일. */}
                  {use_amazingpass ? (
                    <div className="flex items-baseline justify-end gap-1.5 font-[Gmarket-B] tabular-nums leading-none">
                      <span className={cn("text-lg font-bold transition-colors duration-200", exceedsPassLimit ? "text-[#dc2626]" : "text-[#f6475f]")}>선택 {selectedSpotId.length}</span>
                      <span className="text-lg font-bold text-gray-900">/ 보유 {usablePassCount}</span>
                    </div>
                  ) : (
                    <>
                      <span className="text-xs text-gray-400 mb-0.5">예상 결제금액</span>
                      <div className="text-lg font-bold text-[#e16036] font-[Gmarket-B]">
                        {numberWithCommas((selectedPackage?.price || 0) * (requiresSpotSelection ? selectedSpotId.length : (selectedPackage ? 1 : 0)))}원
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* 어메이징패스 예약은 헤더의 뒤로가기(‹)가 스텝 뒤로가기를 담당하므로 하단 "이전"을 숨긴다.
                  일반 예약 플로우는 헤더에 뒤로가기가 없어 이 버튼이 유일한 스텝 뒤로가기 수단이라 유지. */}
              {currentStep > STEP_DATE && !use_amazingpass && (
                <Button
                  type="button"
                  className="shrink-0 py-4 px-4 text-[15px] font-bold rounded-2xl border border-gray-200 bg-white text-gray-700 transition-[scale,background-color] duration-200 active:scale-[0.96] active:bg-gray-100 font-[Gmarket-M]"
                  onClick={handleBack}
                >
                  이전
                </Button>
              )}
              {currentStep === STEP_PRODUCT && (
                <button
                  type="button"
                  className="shrink-0 w-12 h-14 flex items-center justify-center rounded-2xl text-[#f6475f] hover:text-[#e02e48] active:scale-[0.96] transition-[scale,color] duration-200 disabled:text-gray-300 disabled:cursor-not-allowed"
                  onClick={() => setSelectedSpotId([])}
                  disabled={selectedSpotId.length === 0}
                  aria-label="선택 초기화"
                >
                  <Trash2 size={24} strokeWidth={1.8} />
                </button>
              )}
              <Button
                className={cn(
                  "inline-flex items-center justify-center gap-2 flex-1 py-6 text-[17px] font-bold rounded-2xl border-0 transition-[scale,background-color,box-shadow] duration-200 active:scale-[0.96] text-white font-[Gmarket-M]",
                  (currentStep === STEP_DATE && !canMoveToProduct)
                    ? "bg-gray-300 text-gray-500 active:bg-gray-300 shadow-none"
                    : cn(accentBg, accentBgActive, "shadow-[0_6px_16px_-6px_rgba(225,96,54,0.32)]"),
                  ((currentStep === STEP_DATE && !canMoveToProduct) ||
                    (currentStep === STEP_PRODUCT && !canMoveToCheckout) ||
                    (currentStep === STEP_CHECKOUT && !canSubmitPayment)) && "opacity-40 cursor-not-allowed"
                )}
                onClick={() => {
                  // 어메이징패스는 STEP_CHECKOUT(결제 확인) 단계를 두지 않고, 좌석 선택 후 곧바로
                  // 최종 확인/예약 실행 페이지(/amazingpass/order)로 이동한다.
                  if (currentStep === STEP_CHECKOUT || (use_amazingpass && currentStep === STEP_PRODUCT)) {
                    getDetailPage();
                    return;
                  }
                  handleNext();
                }}
                disabled={
                  (currentStep === STEP_DATE && !canMoveToProduct) ||
                  (currentStep === STEP_PRODUCT && !canMoveToCheckout) ||
                  (currentStep === STEP_CHECKOUT && !canSubmitPayment)
                }
              >
                {currentStep === STEP_DATE && "예약 조회"}
                {currentStep === STEP_PRODUCT && (use_amazingpass ? "다음" : "결제 확인")}
                {currentStep === STEP_CHECKOUT && "결제하기"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={waitingModalVisible}
        toggle={() => {
          setSelectedWaitingSpotName("");
          setWaitingModalVisible(!waitingModalVisible);
        }}
        backdrop={"static"}
        centered
        contentClassName="rounded-2xl border-0 overflow-hidden"
      >
        <ModalHeader className="border-b border-gray-100 bg-white">
          <span className="font-bold">알림</span>
          <button
            type="button"
            className="absolute right-4 top-4 text-gray-400 transition-[color,scale] duration-150 ease-out hover:text-gray-600 active:scale-90"
            onClick={() => {
              setSelectedWaitingSpotName("");
              setWaitingModalVisible(!waitingModalVisible);
            }}
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </ModalHeader>
        <ModalBody className="text-center p-6 pt-8">
          <h5 className="font-bold text-lg mb-2" style={{ fontFamily: "Gmarket-B" }}>
            예약대기 기능은 앱으로만 가능합니다.
          </h5>
          <p className="text-gray-500 mb-6">
            스토어에서 어메이징티켓을 앱을 다운받아서<br />대기 기능을 이용해 보세요!
          </p>

          <div className="flex justify-center items-center gap-3">
            {!isIOS && (
              <a href={android_link} className="transition-[opacity,scale] duration-150 ease-out hover:opacity-80 active:scale-[0.96]">
                <img height="48" src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" alt="Google Play" className="h-12 w-auto" />
              </a>
            )}
            {!isAndroid && (
              <a href={ios_link} className="transition-[opacity,scale] duration-150 ease-out hover:opacity-80 active:scale-[0.96]">
                <img height="48" src="https://developer.apple.com/app-store/marketing/guidelines/images/badge-example-preferred_2x.png" alt="App Store" className="h-[3.0rem] w-auto border border-gray-300 rounded-[7px]" />
              </a>
            )}
          </div>
        </ModalBody>
      </Modal>

      {use_amazingpass && (
        <PackageDetailModal
          product={detailProduct}
          isOpen={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          onConfirm={() => {
            if (!detailProduct) {
              setDetailModalOpen(false);
              return;
            }
            const targetId = detailProduct.id;
            setToggleId(targetId);
            setSelectedSpotId([]);
            setDetailModalOpen(false);
            // 모달 닫힘 애니메이션 후, 해당 상품 카드로 스크롤 + 잠깐 강조
            setTimeout(() => {
              document
                .getElementById(`amazingpass-item-${targetId}`)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 260);
          }}
        />
      )}

      {use_amazingpass && (
        <Modal isOpen={seatMapModalOpen} toggle={() => setSeatMapModalOpen(false)} variant="full" className="gap-0 border-0 bg-white">
          <div className="flex min-h-full flex-col bg-white">
            <ModalHeader toggle={() => setSeatMapModalOpen(false)} showBack>
              좌석 선택
            </ModalHeader>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {seatLayout && seatLayout.seats?.length ? (
                <SeatLayoutZoomViewer
                  layout={seatLayout}
                  selectedSeatIds={selectedSpotId.map((s) => s.id)}
                  onSelectSeat={(seat) => toggleSpotSelection({ id: seat.spot_id, name: seat.label || seat.spot_name })}
                  viewportClassName="h-[calc(100vh-240px)] min-h-[420px]"
                  autoFit
                />
              ) : (
                <div className="rounded-3xl border border-dashed border-[#eadfce] bg-[#fffaf3] px-4 py-8 text-center text-sm leading-6 text-[#8b8377]">
                  배치도를 불러올 수 없습니다.
                </div>
              )}
            </div>

            <div className="sticky bottom-0 border-t border-gray-100 bg-white/95 px-4 py-3 backdrop-blur-md" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}>
              <p className="mb-2 text-center text-sm font-bold font-[Gmarket-M] tabular-nums">
                <span className={cn(exceedsPassLimit ? "text-[#dc2626]" : "text-[#f6475f]")}>선택 {selectedSpotId.length}</span>
                <span className="text-gray-400"> / 사용가능 패스 {usablePassCount}</span>
              </p>
              <Button
                className="w-full py-4 text-[15px] font-bold rounded-2xl border-0 bg-[#f6475f] text-white transition-[scale] duration-150 active:scale-[0.98] font-[Gmarket-M]"
                onClick={() => setSeatMapModalOpen(false)}
              >
                선택 완료
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Calendar;
